import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';

type BookingRow = Database['public']['Tables']['bookings']['Row'];
type TeeTimeRow = Database['public']['Tables']['tee_times']['Row'];
type CourseRow = Database['public']['Tables']['courses']['Row'];

const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1725724767938-26e57f67a12c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkaXNjJTIwZ29sZiUyMGNvdXJzZXxlbnwxfHx8fDE3NjA3Mzk4NjZ8MA&ixlib=rb-4.1.0&q=80&w=1080';

export interface BookingData {
  id: string;
  courseId: string;
  courseName: string;
  location: string;
  date: string;
  time: string;
  slotStart: string;
  players: number;
  totalPrice: number;
  distroFundContribution: number;
  status: string;
  confirmationNumber: string;
  courseImage: string;
  refundHoursSnapshot: number | null;
  refundEligibleUntil: string | null;
  refundedAt: string | null;
  hasReview?: boolean;
}

export interface UserBookingsResult {
  upcoming: BookingData[];
  past: BookingData[];
}

function mapBookingStatus(dbStatus: string): string {
  switch (dbStatus) {
    case 'confirmed':
      return 'confirmed';
    case 'requested':
      return 'requested';
    case 'reserved':
      return 'confirmed'; // legacy
    case 'paid':
      return 'confirmed'; // legacy
    case 'cancelled':
      return 'cancelled';
    case 'refunded':
      return 'refunded';
    default:
      return dbStatus;
  }
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatTime(isoString: string, timezone: string): string {
  return new Date(isoString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  });
}

export async function fetchUserBookings(playerId: string): Promise<UserBookingsResult> {
  const { data: bookings, error } = await supabase
    .from('bookings')
    .select('*')
    .eq('player_id', playerId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching bookings:', error);
    return { upcoming: [], past: [] };
  }

  if (!bookings || bookings.length === 0) {
    return { upcoming: [], past: [] };
  }

  // Fetch courses using course_id directly from bookings
  const courseIds = [...new Set(bookings.map((b: any) => b.course_id))];
  const { data: courses } = await supabase
    .from('courses')
    .select('id, name, location, photo_main_path, timezone')
    .in('id', courseIds);

  const courseMap: Record<string, any> = {};
  if (courses) {
    courses.forEach((course: any) => {
      courseMap[course.id] = course;
    });
  }

  const now = new Date();
  const upcomingBookings: BookingData[] = [];
  const pastBookings: BookingData[] = [];

  bookings.forEach((booking: any) => {
    const course = courseMap[booking.course_id];
    if (!course) return;

    const slotStart = new Date(booking.slot_start);
    const isActive = !['cancelled', 'refunded'].includes(booking.status);
    const isUpcoming = slotStart >= now && isActive;

    const mappedStatus = mapBookingStatus(booking.status);
    const status = isActive && slotStart < now ? 'completed' : mappedStatus;

    const bookingData: BookingData = {
      id: booking.id,
      courseId: booking.course_id,
      courseName: course.name,
      location: course.location || 'Unknown Location',
      date: formatDate(booking.slot_start),
      time: formatTime(booking.slot_start, course.timezone || 'America/New_York'),
      slotStart: booking.slot_start,
      players: booking.qty,
      totalPrice: booking.total_dollar_amount,
      distroFundContribution: parseFloat(booking.distro_fund_contribution) || 0,
      status,
      confirmationNumber: (booking as any).confirmation_number || booking.id.slice(0, 5).toUpperCase(),
      courseImage: course.photo_main_path
        ? supabase.storage.from('course-photos').getPublicUrl(course.photo_main_path).data.publicUrl
        : DEFAULT_IMAGE,
      refundHoursSnapshot: (booking as any).refund_hours_snapshot ?? null,
      refundEligibleUntil: (booking as any).refund_eligible_until ?? null,
      refundedAt: (booking as any).refunded_at ?? null,
      hasReview: false
    };

    if (isUpcoming) {
      upcomingBookings.push(bookingData);
    } else {
      pastBookings.push(bookingData);
    }
  });

  upcomingBookings.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  pastBookings.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Mark which past bookings already have a review
  const pastIds = pastBookings.map(b => b.id);
  if (pastIds.length > 0) {
    const { data: reviews } = await supabase
      .from('reviews')
      .select('booking_id')
      .in('booking_id', pastIds);
    const reviewedIds = new Set((reviews || []).map(r => r.booking_id));
    for (const b of pastBookings) {
      b.hasReview = reviewedIds.has(b.id);
    }
  }

  return {
    upcoming: upcomingBookings,
    past: pastBookings
  };
}
